"use client";

import { Dialog, DialogContent } from "design-system/dialog";
import dynamic from "next/dynamic";
import { useState } from "react";
import { useCanCreateEmbeddedWallet } from "@/lib/hooks/flags/use-can-create-embedded-wallet";
import type { UseDialogReturn } from "@/lib/hooks/use-dialog";
import { LoadingTurnkeyView } from "@/subsystems/access/turnkey/components/views/loading-turnkey-view";
import type { RemoveSignerDialogData } from "./remove-signer-flow";

export type { RemoveSignerDialogData };

// The flow pulls in the Turnkey SDK; load it only when the dialog first opens, like the auth dialog.
const RemoveSignerFlow = dynamic(() => import("./remove-signer-flow").then((mod) => mod.RemoveSignerFlow), {
  loading: () => <LoadingTurnkeyView />,
  ssr: false,
});

export function RemoveSignerDialog({ dialogControls }: { dialogControls: UseDialogReturn<RemoveSignerDialogData> }) {
  const enabled = useCanCreateEmbeddedWallet();
  // The flow locks the modal while the Turnkey writes run, so a close can't leave a half-removed signer.
  const [locked, setLocked] = useState(false);
  // Never unmount an open dialog on a transient flag read; see CreateEmbeddedWalletDialog.
  if (!enabled && !dialogControls.props.open) return null;
  const data = dialogControls.data;

  return (
    <Dialog
      onOpenChange={(open) => {
        if (!open && locked) return;
        dialogControls.props.onOpenChange(open);
      }}
      open={dialogControls.props.open}
    >
      <DialogContent
        className="sm:max-w-xl"
        onEscapeKeyDown={(event) => {
          if (locked) event.preventDefault();
        }}
        onInteractOutside={(event) => {
          if (locked) event.preventDefault();
        }}
      >
        {dialogControls.props.open && data ? (
          <RemoveSignerFlow data={data} onClose={dialogControls.dismiss} onLockedChange={setLocked} />
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
